import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Camera, ImagePlus, Loader2, Play, Square, Wallet } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { isPlainObject } from "@/lib/checklistJson";
import {
  completeExtraJob,
  fetchLocationExtraJobs,
  startAssignedExtraJob,
  startEmployeeExtraJob,
  uploadExtraJobPhoto,
  type CleaningExtraJob,
} from "@/lib/extraPaidWork";

interface ExtraPaidSessionSectionProps {
  locationId: string;
  orgId: string | null;
  qrCodeToken?: string | null;
  locationMasterId?: string | null;
}

function formatElapsed(startedAt: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 60_000));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours <= 0) return `${rest} min`;
  return `${hours} godz. ${rest} min`;
}

export function ExtraPaidSessionSection({
  locationId,
  orgId,
  qrCodeToken,
  locationMasterId,
}: ExtraPaidSessionSectionProps) {
  const [jobs, setJobs] = useState<CleaningExtraJob[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [stopJob, setStopJob] = useState<CleaningExtraJob | null>(null);
  const [workReport, setWorkReport] = useState("");
  const [selfTitle, setSelfTitle] = useState("Praca ekstra");
  const [photoCount, setPhotoCount] = useState(0);
  const [tick, setTick] = useState(0);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const loadJobs = useCallback(async () => {
    const { data } = await supabase.auth.getUser();
    const uid = data.user?.id ?? null;
    setUserId(uid);
    if (!uid) return;
    try {
      const rows = await fetchLocationExtraJobs(locationId, uid);
      setJobs(rows.filter((job) => job.taskStatus !== "done" && job.taskStatus !== "cancelled"));
    } catch (error) {
      console.error("[ExtraPaidSessionSection] load", error);
    }
  }, [locationId]);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  const activeJob = jobs.find((job) => job.taskStatus === "in_progress" && job.startedAt);

  useEffect(() => {
    if (!activeJob?.startedAt) return;
    const id = window.setInterval(() => setTick((value) => value + 1), 10_000);
    return () => window.clearInterval(id);
  }, [activeJob?.startedAt]);

  const verifyGpsIfNeeded = async (required: boolean): Promise<{ lat: number | null; lng: number | null }> => {
    if (!required) return { lat: null, lng: null };
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Brak GPS"));
        return;
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 15_000,
        maximumAge: 0,
      });
    });
    const lat = position.coords.latitude;
    const lng = position.coords.longitude;
    const { data, error } = await supabase.rpc("check_location_proximity", {
      p_location_id: locationId,
      p_user_latitude: lat,
      p_user_longitude: lng,
    });
    if (error) throw error;
    const payload = isPlainObject(data) ? data : {};
    const within = payload.is_within_range === true;
    if (!within) {
      const distance = Number(payload.distance ?? payload.distance_meters ?? 0);
      throw new Error(`Jesteś za daleko od obiektu (${Math.round(distance)} m). Podejdź bliżej.`);
    }
    return { lat, lng };
  };

  const verifyQrIfNeeded = async (required: boolean) => {
    if (!required) return;
    const scanned = window.prompt("Zeskanuj lub wpisz kod QR obiektu");
    if (!scanned) throw new Error("Wymagany jest kod QR.");
    const token = scanned.includes("/") ? scanned.split("/").pop() ?? scanned : scanned;
    const options = [locationId, qrCodeToken ?? "", locationMasterId ?? ""]
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    if (!options.includes(token.trim().toLowerCase())) {
      throw new Error("Nieprawidłowy kod QR.");
    }
  };

  const verifyBiometricIfNeeded = async (required: boolean) => {
    if (!required) return;
    if (!window.PublicKeyCredential) {
      toast.info("To urządzenie nie obsługuje biometrii — pomijam ten krok.");
      return;
    }
    const challenge = new Uint8Array(32);
    crypto.getRandomValues(challenge);
    await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: { name: "Domio Cleaning" },
        user: { id: new Uint8Array(16), name: "cleaner", displayName: "Pracownik" },
        pubKeyCredParams: [{ type: "public-key", alg: -7 }],
        authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required" },
        timeout: 30_000,
      },
    });
  };

  const handleStartAssigned = async (job: CleaningExtraJob) => {
    if (!orgId || !userId) return;
    setIsLoading(true);
    try {
      await verifyQrIfNeeded(job.validation.qrRequired);
      await verifyBiometricIfNeeded(job.validation.biometricRequired);
      const gps = await verifyGpsIfNeeded(job.validation.gpsRequired);
      await startAssignedExtraJob({ job, userId, latitude: gps.lat, longitude: gps.lng });
      toast.success("Praca ekstra rozpoczęta.");
      await loadJobs();
    } catch (error) {
      console.error("[ExtraPaidSessionSection] start assigned", error);
      toast.error(error instanceof Error ? error.message : "Nie udało się rozpocząć pracy ekstra.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartSelf = async () => {
    if (!orgId || !userId) return;
    setIsLoading(true);
    try {
      await verifyGpsIfNeeded(true);
      await startEmployeeExtraJob({
        orgId,
        locationId,
        userId,
        title: selfTitle.trim() || "Praca ekstra",
        latitude: null,
        longitude: null,
      });
      toast.success("Praca ekstra zgłoszona. Po zakończeniu koordynator zaakceptuje rozliczenie.");
      await loadJobs();
    } catch (error) {
      console.error("[ExtraPaidSessionSection] start self", error);
      toast.error(error instanceof Error ? error.message : "Nie udało się rozpocząć pracy ekstra.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoto = async (file: File | undefined, source: "camera" | "gallery") => {
    if (!file || !stopJob || !orgId || !userId) return;
    try {
      await uploadExtraJobPhoto({
        orgId,
        jobId: stopJob.id,
        userId,
        file,
        source,
        sortOrder: photoCount,
      });
      setPhotoCount((count) => count + 1);
      toast.success("Zdjęcie dodane.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nie udało się dodać zdjęcia.");
    }
  };

  const handleComplete = async () => {
    if (!stopJob) return;
    if (!workReport.trim()) {
      toast.error("Opisz, co zostało wykonane.");
      return;
    }
    if (stopJob.validation.requiresPhoto && photoCount < 1) {
      toast.error("To zlecenie wymaga zdjęcia.");
      return;
    }
    setIsLoading(true);
    try {
      await completeExtraJob(stopJob.id, workReport.trim());
      toast.success("Praca ekstra zakończona.");
      setStopJob(null);
      setWorkReport("");
      setPhotoCount(0);
      await loadJobs();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nie udało się zakończyć pracy.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="border-2 border-amber-400 bg-white shadow-md dark:border-amber-700 dark:bg-zinc-900">
      <CardHeader className="pb-3 bg-amber-100 dark:bg-amber-950/60 rounded-t-xl">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-amber-700" />
          <h3 className="text-sm font-bold text-amber-950 dark:text-amber-100">Prace ekstra płatne</h3>
        </div>
        <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
          Osobna rejestracja czasu, niezależna od sprzątania. Widoczna zawsze.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {activeJob && (
          <div className="rounded-lg border bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="font-medium text-sm">{activeJob.title}</p>
                <p className="text-xs text-muted-foreground">
                  Czas: {formatElapsed(activeJob.startedAt ?? new Date().toISOString())}
                  <span className="sr-only">{tick}</span>
                </p>
              </div>
              <Badge variant="secondary">W trakcie</Badge>
            </div>
            <Button
              className="w-full"
              variant="destructive"
              disabled={isLoading}
              onClick={() => {
                setStopJob(activeJob);
                setWorkReport("");
                setPhotoCount(0);
              }}
            >
              <Square className="h-4 w-4 mr-2" />
              Zakończ pracę ekstra
            </Button>
          </div>
        )}

        {!activeJob &&
          jobs
            .filter((job) => job.taskStatus === "pending")
            .map((job) => (
              <div key={job.id} className="rounded-lg border p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">{job.title}</p>
                    {job.instructions && (
                      <p className="text-xs text-muted-foreground mt-1">{job.instructions}</p>
                    )}
                  </div>
                  <Badge>{job.origin === "coordinator" ? "Zlecenie" : "Zgłoszenie"}</Badge>
                </div>
                <Button className="w-full" disabled={isLoading} onClick={() => void handleStartAssigned(job)}>
                  {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  Rozpocznij
                </Button>
              </div>
            ))}

        {!activeJob && (
          <div className="space-y-2 pt-1">
            <Label htmlFor="extra-self-title">Start bez zlecenia (np. po telefonie)</Label>
            <Input
              id="extra-self-title"
              value={selfTitle}
              onChange={(e) => setSelfTitle(e.target.value)}
              placeholder="Co masz wykonać?"
            />
            <Button variant="outline" className="w-full" disabled={isLoading || !orgId} onClick={() => void handleStartSelf()}>
              Rozpocznij pracę ekstra
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={Boolean(stopJob)} onOpenChange={(open) => !open && setStopJob(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Zakończ pracę ekstra</DialogTitle>
            <DialogDescription>
              Opisz, co zostało wykonane. Zdjęcia możesz dodać z aparatu lub galerii.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={workReport}
              onChange={(e) => setWorkReport(e.target.value)}
              placeholder="Np. wymiana żarówek na klatce B, 3. piętro"
              rows={4}
            />
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => cameraRef.current?.click()}>
                <Camera className="h-4 w-4 mr-2" />
                Aparat
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => galleryRef.current?.click()}>
                <ImagePlus className="h-4 w-4 mr-2" />
                Galeria
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Dodane zdjęcia: {photoCount}</p>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                void handlePhoto(e.target.files?.[0], "camera");
                e.target.value = "";
              }}
            />
            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                void handlePhoto(e.target.files?.[0], "gallery");
                e.target.value = "";
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStopJob(null)}>
              Anuluj
            </Button>
            <Button disabled={isLoading} onClick={() => void handleComplete()}>
              {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Zakończ i wyślij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
